import React, { useState } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { getAuthToken } from '../services/api';
import { XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';
import ApplicationDocuments from './ApplicationDocuments';

interface UpdateApplicationStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  currentStatus: string;
  onStatusUpdated: () => void;
}

const UpdateApplicationStatusModal: React.FC<UpdateApplicationStatusModalProps> = ({
  isOpen,
  onClose,
  applicationId,
  currentStatus,
  onStatusUpdated
}) => {
  const [formData, setFormData] = useState({
    status: currentStatus,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDocuments, setShowDocuments] = useState(false);

  const statuses = [
    { value: 'submitted', label: 'Submitted', description: 'Application received' },
    { value: 'under_review', label: 'Under Review', description: 'Being reviewed by admin' },
    { value: 'approved', label: 'Approved', description: 'Application approved - ready for enrollment' },
    { value: 'enrolled', label: 'Enrolled', description: 'Student enrolled - ready to sync to ERP' },
    { value: 'rejected', label: 'Rejected', description: 'Application rejected' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update status');
      }

      setFormData({
        status: currentStatus,
        notes: ''
      });

      onStatusUpdated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (showDocuments) {
    return (
      <ApplicationDocuments
        applicationId={applicationId}
        studentName="Student"
        onClose={() => setShowDocuments(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">Update Application Status</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Application Status
            </label>
            <div className="space-y-2">
              {statuses.map((status) => (
                <label
                  key={status.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.status === status.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={status.value}
                    checked={formData.status === status.value}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{status.label}</div>
                    <div className="text-sm text-gray-500">{status.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-field"
              rows={3}
              placeholder="Add any notes about this status change..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowDocuments(true)}
              className="btn-outline flex items-center gap-2"
              disabled={loading}
            >
              <DocumentIcon className="h-4 w-4" />
              Documents
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateApplicationStatusModal;
